import {
  Avatar,
  Button,
  Chip,
  Collapse,
  Divider,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  Paper,
  Switch,
  TextField,
  Typography
} from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { AddRounded, ClearRounded } from '@material-ui/icons';
import { Autocomplete } from '@material-ui/lab';
import { unwrapResult } from '@reduxjs/toolkit';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import useProjectUsers from '../../hooks/useProjectUsers';
import { Redirect, useHistory } from 'react-router-dom';
import {
  ProjectWorkflow,
  WorkflowStepWithCheck
} from '../../api/project-workflows';
import { RootState } from '../../app/store';
import HelpTooltip from '../../components/HelpTooltip';
import ProjectInfoCard from '../../components/ProjectInfoCard';
import mapExistingUserInWorkflowStep from '../../functions/mapExistingUserInWorkflowStep';
import { useAppDispatch } from '../../hooks/useAppRudux';
import {
  useQueryStringNumber,
  useQueryStringText
} from '../../hooks/useQueryString';
import Breadcrumb from '../Breadcrumb';
import loadingSlice from '../Loading/loadingSlice';
import {
  Workflow,
  getWorkflowsAsync
} from '../MasterData/Workflow/workflowSlice';
import { WorkflowStep } from '../MasterData/WorkflowStep/workflowStepSlice';
import { getProjectAsync } from '../Project/projectSlice';
import { User } from '../UserProfile/userProfileSlice';
import {
  createProjectWorkflowAsync,
  getProjectWorkflowAsync
} from './projectWorkflowSlice';

export default function WorkflowCreate() {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const users = useProjectUsers();
  const { data: disciplines } = useSelector(
    (state: RootState) => state.disciplines
  );
  const { data: workflows } = useSelector(
    (state: RootState) => state.workflows
  );
  const { data: workflowSteps } = useSelector(
    (state: RootState) => state.workflowSteps
  );
  const { selectedProjectWorkflow } = useSelector(
    (state: RootState) => state.projectWorkflows
  );
  const { selectedProject } = useSelector((state: RootState) => state.projects);

  const projectId = useQueryStringNumber({ query: 'projectId' });
  const refFlowId = useQueryStringNumber({ query: 'refFlowId' });
  const redirect = useQueryStringText({ query: 'redirect' });
  const defaultWorkflowSteps = workflowSteps.map((workflowStep) => {
    return {
      ...workflowStep,
      isRequired: true,
      users: []
    };
  });
  const [projectWorkflow, setProjectWorkflow] = useState<ProjectWorkflow>({
    name: '',
    discipline: null,
    isCreatorViewOnlyExternal: false,
    isCreatorViewOnlyFinal: false,
    projectWorkflowSteps: [],
    workflow: null
  });
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const defaultWorkflowSteps = workflowSteps.map((workflowStep) => {
      if (refFlowId && selectedProjectWorkflow) {
        const projectWorkflowStep =
          selectedProjectWorkflow.projectWorkflowSteps.find(
            (projectWorkflowStep) =>
              workflowStep.id === projectWorkflowStep.workflowStepId
          );
        let projectApprovalWorkflowStepUsers: User[] = [];
        if (projectWorkflowStep) {
          projectApprovalWorkflowStepUsers = mapExistingUserInWorkflowStep(
            projectWorkflowStep.projectApprovalWorkflowSteps,
            users
          );
        }
        return {
          ...workflowStep,
          isRequired: Boolean(projectWorkflowStep),
          users: projectApprovalWorkflowStepUsers
        };
      }
      return {
        ...workflowStep,
        isRequired: true,
        users: []
      };
    });
    setProjectWorkflow({
      ...projectWorkflow,
      name:
        refFlowId && selectedProjectWorkflow
          ? selectedProjectWorkflow.name
          : projectWorkflow.name,
      discipline:
        refFlowId && selectedProjectWorkflow
          ? disciplines.find(
              (discipline) =>
                selectedProjectWorkflow.disciplineId === discipline.id
            ) ?? null
          : projectWorkflow.discipline,
      isCreatorViewOnlyExternal:
        refFlowId && selectedProjectWorkflow
          ? selectedProjectWorkflow.isCreatorViewOnlyExternal
          : false,
      isCreatorViewOnlyFinal:
        refFlowId && selectedProjectWorkflow
          ? selectedProjectWorkflow.isCreatorViewOnlyFinal
          : false,
      projectWorkflowSteps: defaultWorkflowSteps
    });
  }, [selectedProjectWorkflow, workflowSteps, users]);

  useEffect(() => {
    dispatch(getWorkflowsAsync());
    if (refFlowId) {
      dispatch(getProjectWorkflowAsync(refFlowId));
    }
    if (projectId) {
      dispatch(getProjectAsync(projectId));
    }
  }, []);

  function updateProjectWorkflow(
    newWorkflowValue: Workflow | null,
    projectWorkflowSteps: WorkflowStepWithCheck[]
  ) {
    setProjectWorkflow({
      ...projectWorkflow,
      workflow: newWorkflowValue,
      projectWorkflowSteps
    });
  }

  function onChangeWorkflowTemplate(event: any, newValue: Workflow | null) {
    if (newValue) {
      const workflowById = workflows.find(
        (workflow) => workflow.id === newValue.id
      );
      const newProjectWorkflowStep = projectWorkflow.projectWorkflowSteps.map(
        (projectWorkflowStep) => {
          let isRequired = true;
          if (workflowById) {
            isRequired = workflowById.workflowSteps.some(
              (workflowStep: WorkflowStep) =>
                workflowStep.id === projectWorkflowStep.id
            );
          }
          return { ...projectWorkflowStep, isRequired };
        }
      );
      updateProjectWorkflow(newValue, newProjectWorkflowStep);
    } else {
      updateProjectWorkflow(newValue, defaultWorkflowSteps);
    }
  }

  function onChangeByField(
    projectWorkflowStepId: number,
    newValue: boolean | User[],
    fieldName: string
  ) {
    const { projectWorkflowSteps } = projectWorkflow;
    const projectWorkflowStepIndex = projectWorkflowSteps.findIndex(
      (projectWorkflowStep) => projectWorkflowStep.id === projectWorkflowStepId
    );
    const newProjectWorkflowStep = {
      ...projectWorkflowSteps[projectWorkflowStepIndex],
      [fieldName]: newValue
    };
    const newProjectWorkflowSteps = [
      ...projectWorkflowSteps.slice(0, projectWorkflowStepIndex),
      newProjectWorkflowStep,
      ...projectWorkflowSteps.slice(projectWorkflowStepIndex + 1)
    ];
    setProjectWorkflow({
      ...projectWorkflow,
      projectWorkflowSteps: newProjectWorkflowSteps
    });
  }

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      setLoading(true);
      if (selectedProject) {
        const result = await dispatch(
          createProjectWorkflowAsync({
            project: selectedProject,
            projectWorkflow
          })
        );
        await unwrapResult(result);
        dispatch(
          loadingSlice.actions.success({
            message: t('projectWorkflowCreate.messages.workflowCreatedSuccessfully') as string
          })
        );
        setTimeout(() => {
          redirect
            ? history.push(redirect)
            : history.push(`/projects/${projectId}?tabId=1`);
        }, 1000);
      } else {
        throw new Error(t('projectWorkflowCreate.errors.pleaseSelectProject') as string);
      }
    } catch (error) {
      setLoading(false);
    }
  }

  if (!projectId) {
    return <Redirect to="/projects" />;
  }

  return (
    <div className={classes.root}>
      <Breadcrumb
        name="createProjectWorkflow"
        data={{
          projectName: selectedProject?.name,
          projectId: selectedProject?.id
        }}
      />
      <Typography className={classes.title} component="p" variant="h4">
        {t('projectWorkflowCreate.title')}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={3} className={classes.showInformation}>
          {selectedProject && <ProjectInfoCard project={selectedProject} />}
        </Grid>
        <Grid item xs={12} md={9}>
          <form onSubmit={onSubmit}>
            <Paper className={classes.card}>
              <Typography variant="subtitle1" gutterBottom>
                {t('projectWorkflowCreate.sections.workflow')}
                <HelpTooltip>
                  {t('projectWorkflowCreate.helpTexts.setStandardWorkflow')}
                </HelpTooltip>
              </Typography>
              <Divider className={classes.divider} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={8}>
                  <TextField
                    className={classes.fullWidth}
                    id="flow-name"
                    variant="outlined"
                    size="small"
                    label={t('projectWorkflowCreate.labels.flowName')}
                    value={projectWorkflow.name}
                    onChange={(event) => {
                      setProjectWorkflow({
                        ...projectWorkflow,
                        name: event.target.value
                      });
                    }}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Autocomplete
                    className={classes.fullWidth}
                    id="discipline"
                    options={disciplines}
                    getOptionLabel={(option) => option?.name}
                    value={projectWorkflow.discipline}
                    getOptionSelected={(option, value) =>
                      option.id === value?.id
                    }
                    onChange={(event, newValue) => {
                      setProjectWorkflow({
                        ...projectWorkflow,
                        discipline: newValue
                      });
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        size="small"
                        label={t('projectWorkflowCreate.labels.discipline')}
                        required
                      />
                    )}
                  />
                </Grid>
                {!refFlowId && (
                  <Grid item xs={12}>
                    <Autocomplete
                      className={classes.fullWidth}
                      id="workflow-template"
                      options={workflows}
                      getOptionLabel={(option) => option?.name}
                      value={projectWorkflow.workflow}
                      getOptionSelected={(option, value) =>
                        option.id === value?.id
                      }
                      onChange={onChangeWorkflowTemplate}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          size="small"
                          label={t('projectWorkflowCreate.labels.workflowTemplate')}
                        />
                      )}
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <FormLabel component="legend">
                    {t('projectWorkflowCreate.sections.creatorConfiguration')}
                  </FormLabel>
                  <div>
                    <FormControlLabel
                      className={classes.customizeCreator}
                      control={
                        <Switch
                          checked={projectWorkflow.isCreatorViewOnlyExternal}
                          onChange={(event) =>
                            setProjectWorkflow({
                              ...projectWorkflow,
                              isCreatorViewOnlyExternal: event.target.checked
                            })
                          }
                          name="isCreatorViewOnlyExternal"
                          color="primary"
                        />
                      }
                      label={t('projectWorkflowCreate.labels.showExternalStepOnly')}
                    />
                    <HelpTooltip>
                      {t('projectWorkflowCreate.helpTexts.creatorCanSeeExternal')}{' '}
                      {workflowSteps
                        .filter((workflowStep) => !workflowStep.isInternal)
                        .map((workflowStep) => workflowStep.name)
                        .join(', ')}
                    </HelpTooltip>
                  </div>
                  <div>
                    <FormControlLabel
                      className={classes.customizeCreator}
                      control={
                        <Switch
                          checked={projectWorkflow.isCreatorViewOnlyFinal}
                          onChange={(event) =>
                            setProjectWorkflow({
                              ...projectWorkflow,
                              isCreatorViewOnlyFinal: event.target.checked
                            })
                          }
                          name="isCreatorViewOnlyFinal"
                          color="primary"
                        />
                      }
                      label={t('projectWorkflowCreate.labels.showApprovalCodeOnly')}
                    />
                    <HelpTooltip>
                      {t('projectWorkflowCreate.helpTexts.creatorCanSeeFinal')}{' '}
                      {workflowSteps
                        .filter((workflowStep) => workflowStep.isFinalApprover)
                        .map((workflowStep) => workflowStep.name)
                        .join(', ')}
                    </HelpTooltip>
                  </div>
                  {(projectWorkflow.isCreatorViewOnlyExternal ||
                    projectWorkflow.isCreatorViewOnlyFinal) && (
                    <FormHelperText>
                      {t('projectWorkflowCreate.helpTexts.theCreatorCanSeeOnly')}{' '}
                      {
                        <b>
                          {workflowSteps
                            .filter(
                              ({ isInternal, isFinalApprover }) =>
                                (!projectWorkflow.isCreatorViewOnlyExternal ||
                                  !isInternal) &&
                                (!projectWorkflow.isCreatorViewOnlyFinal ||
                                  isFinalApprover)
                            )
                            .map((workflowStep) => workflowStep.name)
                            .join(', ')}
                        </b>
                      }
                    </FormHelperText>
                  )}
                </Grid>
              </Grid>
            </Paper>
            <Paper className={classes.card}>
              <Typography variant="subtitle1" gutterBottom>
                {t('projectWorkflowCreate.sections.reviewers')}
              </Typography>
              <Divider className={classes.divider} />
              {projectWorkflow.projectWorkflowSteps.map(
                (projectWorkflowStep, index) => {
                  return (
                    <div key={projectWorkflowStep.id}>
                      <div className={classes.flexFlowStep}>
                        <Avatar
                          className={clsx(classes.smallAvatar, {
                            [classes.avatarDisabled]:
                              !projectWorkflowStep.isRequired
                          })}
                        >
                          {index + 1}
                        </Avatar>
                        <Typography
                          className={classes.flexGrowName}
                          variant="subtitle1"
                          color={
                            projectWorkflowStep.isRequired
                              ? 'textPrimary'
                              : 'textSecondary'
                          }
                        >
                          {projectWorkflowStep.name}
                        </Typography>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={projectWorkflowStep?.isRequired}
                              onChange={(event) =>
                                onChangeByField(
                                  projectWorkflowStep.id,
                                  event.target.checked,
                                  'isRequired'
                                )
                              }
                              name={`${projectWorkflowStep.id}`}
                              color="primary"
                            />
                          }
                          label={t('projectWorkflowCreate.labels.required')}
                        />
                      </div>
                      <Collapse in={projectWorkflowStep.isRequired}>
                        <Autocomplete
                          className={classes.userField}
                          id={`userId-${projectWorkflowStep.id}`}
                          size="small"
                          multiple
                          options={users}
                          getOptionLabel={(option) =>
                            `${option?.firstName} ${option?.lastName}`
                          }
                          renderTags={(tagValue, getTagProps) =>
                            tagValue.map((option, index) => (
                              <Chip
                                label={`${option?.firstName} ${option?.lastName}`}
                                {...getTagProps({ index })}
                                variant="outlined"
                                size="small"
                                deleteIcon={<ClearRounded />}
                              />
                            ))
                          }
                          value={projectWorkflowStep.users}
                          getOptionSelected={(option, value) =>
                            option.id === value?.id
                          }
                          onChange={(event, newValue) =>
                            onChangeByField(
                              projectWorkflowStep.id,
                              newValue,
                              'users'
                            )
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              placeholder={
                                projectWorkflowStep.users.length === 0
                                  ? (t('projectWorkflowCreate.placeholders.selectUser') as string)
                                  : ''
                              }
                              required={
                                projectWorkflowStep.isRequired &&
                                projectWorkflowStep.users.length === 0
                              }
                            />
                          )}
                        />
                      </Collapse>
                    </div>
                  );
                }
              )}
            </Paper>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              disabled={loading}
              startIcon={<AddRounded />}
            >
              {t('projectWorkflowCreate.buttons.createProjectWorkflow')}
            </Button>
          </form>
        </Grid>
      </Grid>
    </div>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(1.5, 0, 12, 0)
    },
    title: {
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    card: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      borderRadius: theme.spacing(2)
    },
    divider: {
      margin: theme.spacing(0.5, 0, 2, 0),
      width: theme.spacing(4)
    },
    fullWidth: {
      width: '100%'
    },
    textAlignRight: {
      textAlign: 'right'
    },
    smallAvatar: {
      width: theme.spacing(4),
      height: theme.spacing(4),
      backgroundColor: theme.palette.primary.main
    },
    avatarDisabled: {
      backgroundColor: theme.palette.text.disabled
    },
    flexFlowStep: {
      display: 'flex',
      alignItems: 'center',
      margin: theme.spacing(2, 0)
    },
    flexGrowName: {
      flexGrow: 1,
      padding: theme.spacing(0, 2)
    },
    userField: {
      width: `calc(100% - ${theme.spacing(8)}px)`,
      marginLeft: theme.spacing(6)
    },
    showInformation: {
      [theme.breakpoints.down('sm')]: {
        display: 'none'
      }
    },
    customizeCreator: {
      marginRight: theme.spacing(0)
    }
  })
);
